/**
 * Content State Manager
 * 
 * Manages content state across the editing lifecycle for the Targeted Edit architecture.
 * Tracks original content, current content, operation history, and dirty state.
 * 
 * @module contentStateManager
 */

/**
 * Enumeration of content operation types
 * @enum {string}
 */
export const ContentOperationType = {
    GENERATION: 'generation',
    EDIT: 'edit',
    REGENERATION: 'regeneration',
    REFINEMENT: 'refinement'
};

/**
 * Enumeration of content state actions
 * @enum {string}
 */
export const ContentStateAction = {
    SET_CONTENT: 'SET_CONTENT',
    COMMIT_CHANGES: 'COMMIT_CHANGES',
    RESET: 'RESET',
    SET_OPERATION: 'SET_OPERATION',
    RESTORE_FROM_HISTORY: 'RESTORE_FROM_HISTORY',
    CLEAR_HISTORY: 'CLEAR_HISTORY'
};

/**
 * Create initial content state
 * 
 * @param {string|null} initialContent - Optional initial content
 * @returns {object} Initial state object
 */
export const createContentState = (initialContent = null) => ({
    originalContent: initialContent,
    currentContent: initialContent,
    lastOperation: null,
    operationHistory: [],
    isDirty: false,
    createdAt: Date.now(),
    updatedAt: Date.now()
});

/**
 * Content state reducer for managing state transitions
 * 
 * @param {object} state - Current state
 * @param {object} action - Action object with type and payload
 * @returns {object} New state
 */
export const contentStateReducer = (state, action) => {
    switch (action.type) {
        case ContentStateAction.SET_CONTENT:
            return {
                ...state,
                currentContent: action.payload,
                isDirty: action.payload !== state.originalContent,
                updatedAt: Date.now()
            };

        case ContentStateAction.COMMIT_CHANGES:
            return {
                ...state,
                originalContent: state.currentContent,
                isDirty: false,
                operationHistory: [
                    ...state.operationHistory,
                    {
                        timestamp: Date.now(),
                        operation: state.lastOperation,
                        previousContent: state.originalContent,
                        content: state.currentContent
                    }
                ],
                updatedAt: Date.now()
            };

        case ContentStateAction.RESET:
            return {
                ...state,
                currentContent: state.originalContent,
                isDirty: false,
                lastOperation: null,
                updatedAt: Date.now()
            };

        case ContentStateAction.SET_OPERATION:
            return {
                ...state,
                lastOperation: action.payload,
                updatedAt: Date.now()
            };

        case ContentStateAction.RESTORE_FROM_HISTORY:
            const historyIndex = action.payload;
            if (historyIndex < 0 || historyIndex >= state.operationHistory.length) {
                return state;
            }
            const historyEntry = state.operationHistory[historyIndex];
            return {
                ...state,
                currentContent: historyEntry.content,
                originalContent: historyEntry.previousContent,
                isDirty: true,
                updatedAt: Date.now()
            };

        case ContentStateAction.CLEAR_HISTORY:
            return {
                ...state,
                operationHistory: [],
                updatedAt: Date.now()
            };

        default:
            return state;
    }
};

/**
 * Selectors for accessing state properties
 */
export const contentSelectors = {
    getContent: (state) => state.currentContent,
    getOriginalContent: (state) => state.originalContent,
    isDirty: (state) => state.isDirty,
    getLastOperation: (state) => state.lastOperation,
    getHistoryLength: (state) => state.operationHistory.length,
    canUndo: (state) => state.operationHistory.length > 0,
    hasContent: (state) => state.currentContent && state.currentContent.trim().length > 0
};

/**
 * Action creators for common operations
 */
export const contentActions = {
    setContent: (content) => ({
        type: ContentStateAction.SET_CONTENT,
        payload: content
    }),

    commitChanges: () => ({
        type: ContentStateAction.COMMIT_CHANGES
    }),

    reset: () => ({
        type: ContentStateAction.RESET
    }),

    setOperation: (operation) => ({
        type: ContentStateAction.SET_OPERATION,
        payload: operation
    }),

    restoreFromHistory: (index) => ({
        type: ContentStateAction.RESTORE_FROM_HISTORY,
        payload: index
    }),

    clearHistory: () => ({
        type: ContentStateAction.CLEAR_HISTORY
    })
};

/**
 * Calculate content statistics
 * 
 * @param {string} content - Content to analyze
 * @returns {object} Statistics object
 */
export const calculateContentStats = (content) => {
    if (!content) {
        return {
            characters: 0,
            words: 0,
            paragraphs: 0,
            hashtags: 0,
            mentions: 0,
            emojis: 0
        };
    }

    const characters = content.length;
    const words = content.trim().split(/\s+/).filter(Boolean).length;
    const paragraphs = content.split(/\n\n+/).filter(Boolean).length;
    const hashtags = (content.match(/#[\w]+/g) || []).length;
    const mentions = (content.match(/@[\w]+/g) || []).length;

    // Simple emoji detection (covers most common emoji ranges)
    const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu;
    const emojis = (content.match(emojiRegex) || []).length;

    return {
        characters,
        words,
        paragraphs,
        hashtags,
        mentions,
        emojis
    };
};

/**
 * Compare two content versions and return diff info
 * 
 * @param {string} original - Original content
 * @param {string} modified - Modified content
 * @returns {object} Diff information
 */
export const compareContent = (original, modified) => {
    const originalStats = calculateContentStats(original || '');
    const modifiedStats = calculateContentStats(modified || '');

    const lengthDiff = modifiedStats.characters - originalStats.characters;
    const lengthChangePercent = originalStats.characters > 0
        ? ((lengthDiff / originalStats.characters) * 100).toFixed(1)
        : 0;

    return {
        originalStats,
        modifiedStats,
        lengthDiff,
        lengthChangePercent: parseFloat(lengthChangePercent),
        isLonger: lengthDiff > 0,
        isShorter: lengthDiff < 0,
        isSameLength: lengthDiff === 0,
        structuralChange: originalStats.paragraphs !== modifiedStats.paragraphs
    };
};

export default {
    ContentOperationType,
    ContentStateAction,
    createContentState,
    contentStateReducer,
    contentSelectors,
    contentActions,
    calculateContentStats,
    compareContent
};
